import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CanvasPreview from "../components/CanvasPreview";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";
import Alert from "../components/Alert";
import { useTour } from "../contexts/TourContext";
import ToursMenu from "../components/ToursMenu";

const TILE_SIZE = { width: 200, height: 120 };

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const showImages = user?.show_images !== undefined ? user.show_images : true;
  const navigate = useNavigate();
  const [canvases, setCanvases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [previewCards, setPreviewCards] = useState({});
  const [symbols, setSymbols] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const { startTour } = useTour();

  // Load saved user selection from sessionStorage on initial load
  useEffect(() => {
    if (user?.role === 'admin') {
      const saved = sessionStorage.getItem('dashboard_selected_user_id');
      if (saved) {
        setSelectedUserId(parseInt(saved, 10));
      }
    }
  }, [user]);

  // Save user selection to sessionStorage when it changes
  useEffect(() => {
    if (user?.role === 'admin' && selectedUserId !== null) {
      sessionStorage.setItem('dashboard_selected_user_id', selectedUserId.toString());
    } else if (user?.role === 'admin' && selectedUserId === null) {
      sessionStorage.removeItem('dashboard_selected_user_id');
    }
  }, [user, selectedUserId]);

  useEffect(() => {
    if (user && !localStorage.getItem('sw_tour_done')) {
      startTour('intro');
    }
  }, [user, startTour]);

  // Fetch users for admin dropdown
  useEffect(() => {
    if (user?.role === 'admin') {
      api.get('/api/admin/users')
        .then(res => setUsers(Array.isArray(res.data) ? res.data : []))
        .catch(err => console.error('Failed to fetch users:', err));
    }
  }, [user]);

  useEffect(() => {
    const fetchCanvases = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        if (selectedUserId) {
          params.append('userId', selectedUserId);
        }
        const res = await api.get(`/api/canvas?${params.toString()}`);
        const canvasesData = Array.isArray(res.data) ? res.data : [];
        setCanvases(canvasesData);

        // Fetch preview cards for each canvas in parallel
                const cardsData = {};
                const previewPromises = canvasesData.map(async (canvas) => {
                  try {
                    const canvasRes = await api.get(`/api/canvas/${canvas.canvas_id}`);
                    return { canvasId: canvas.canvas_id, cards: canvasRes.data.cards || [] };
                  } catch (err) {
                    console.error(`Error fetching preview for canvas ${canvas.canvas_id}:`, err);
                    return { canvasId: canvas.canvas_id, cards: [] };
                  }
                });

        const previewResults = await Promise.all(previewPromises);
        previewResults.forEach(({ canvasId, cards }) => {
          cardsData[canvasId] = cards;
        });
        setPreviewCards(cardsData);
      } catch (err) {
        console.error("Dashboard fetchCanvases error:", err);
        setError(err.message || "Failed to load canvases");
        setCanvases([]);
      } finally {
        setLoading(false);
      }
    };
    if (user) {
      fetchCanvases();
      // Fetch symbols for previews
      api.get('/api/symbols').then(res => setSymbols(Array.isArray(res.data) ? res.data : [])).catch(err => console.error("Dashboard symbols error:", err));
    }
    else if (!authLoading) setLoading(false);
  }, [user, authLoading, selectedUserId]);

  useEffect(() => {
    if (!user && !authLoading && !loading && !error) {
      navigate('/login');
    }
  }, [user, authLoading, loading, navigate, error]);

  // Create a new canvas with user defaults
  const handleCreateNewCanvas = async () => {
    if (!user) return;
    setCreating(true);
    setError("");
    try {
      // Use user preferences for language and shuffle_extensions
      const language = user.language || "en";
      const shuffle_extensions = user.default_shuffle_extensions !== undefined ? user.default_shuffle_extensions : true;
      // By default, include all user's decks
      const res = await api.post("/api/canvas", { language, shuffle_extensions, decks: [] });
      // Navigate to the new canvas
      navigate(`/canvas/${res.data.canvasId}`);
    } catch (err) {
      setError(err.message || "Failed to create canvas");
    } finally {
      setCreating(false);
    }
  };

  const handleCanvasClick = (canvas) => {
    navigate(`/canvas/${canvas.canvas_id}`);
  };

  const isActuallyLoading = authLoading || loading;

  return (
          <div style={{ height: "100vh", width: "100vw", padding: 16 }}>
            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <h1 style={{ margin: 0 }}>Dashboard</h1>
                <ToursMenu isCanvas={false} />
                {user?.role === 'admin' && users.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8, 
                      color: 'var(--text-secondary)',
                      fontSize: 14,
                      fontWeight: 500
                    }}>
                      View as:
                      <select
                        value={selectedUserId || user.user_id}
                        onChange={(e) => setSelectedUserId(e.target.value ? parseInt(e.target.value) : null)}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid var(--border)',
                          borderRadius: 4,
                          background: 'var(--surface)',
                          color: 'var(--text)',
                          fontSize: 14,
                          minWidth: 220,
                          cursor: 'pointer'
                        }}
                      >
                        {users.map(u => (
                          <option key={u.user_id} value={u.user_id}>
                            {u.nickname} ({u.email})
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div>
          <Alert type="error" message={error} />

          {isActuallyLoading && !error ? (
          <div style={{ marginTop: 24 }}>
            <h2 style={{ marginBottom: 24, color: 'var(--text)' }}>Your Canvases</h2>
            <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
          </div>
        ) : !error && user ? (
                  <div>
                    <h2 style={{ marginBottom: 0, color: 'var(--text)' }}>Your Canvases</h2>

                    <Alert type="error" message={error} />

            {isActuallyLoading && !error ? (
              <div style={{ marginTop: 24 }}>
                <h2 style={{ marginBottom: 24, color: 'var(--text)' }}>Your Canvases</h2>
                <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
              </div>
            ) : !error && user ? (
              <div id="tour-your-canvases" style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 32,
                alignItems: "start"
              }}>
                {/* Canvases */}
                {canvases.length === 0 ? (
                  <div style={{ color: "var(--text-light)", fontSize: 18, textAlign: "center", gridColumn: "1/-1", padding: '40px' }}>
                    No canvases yet. Create your first one!
                  </div>
                ) : (
                  canvases.map((canvas) => (
                    <div
                      key={canvas.canvas_id}
                      style={{
                        background: "var(--surface)",
                        border: "1px solid var(--border)",
                        borderRadius: 24,
                        boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
                        padding: 20,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        position: "relative",
                        width: '100%',
                        maxWidth: 240,
                        height: 240,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.borderColor = 'var(--primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.borderColor = 'var(--border)';
                      }}
                      onClick={() => handleCanvasClick(canvas)}
                    >
                      <div style={{
                        position: "relative",
                        width: '100%',
                        height: '100%',
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                        <CanvasPreview
                          cards={previewCards[canvas.canvas_id] || []}
                          width={180}
                          height={180}
                          symbols={symbols}
                          showImages={showImages}
                          language={user?.language || 'en'}
                        />
                        <div style={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: "linear-gradient(transparent, rgba(15, 23, 42, 0.9))",
                          padding: "40px 12px 12px 12px",
                          color: "var(--text)",
                          fontWeight: 600,
                          fontSize: 16,
                          textAlign: "center",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                          lineHeight: 1.3,
                          borderRadius: '0 0 24px 24px'
                        }}>{canvas.name || 'Untitled Canvas'}</div>
                      </div>
                    </div>
                  ))
                )}
                {/* Create new tile */}
                <div
                  id="tour-create-canvas"
                  style={{
                    background: "rgba(99, 102, 241, 0.05)",
                    border: "2px dashed var(--border)",
                    borderRadius: 24,
                    padding: 20,
                    cursor: creating ? "wait" : "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: '100%',
                    maxWidth: 240,
                    height: 240,
                    opacity: creating ? 0.6 : 1,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!creating) {
                      e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
                      e.currentTarget.style.borderColor = 'var(--primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!creating) {
                      e.currentTarget.style.background = 'rgba(99, 102, 241, 0.05)';
                      e.currentTarget.style.borderColor = 'var(--border)';
                    }
                  }}
                  onClick={creating ? undefined : handleCreateNewCanvas}
                >
                  <div style={{
                    width: 120,
                    height: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 64,
                    color: "var(--primary)",
                    background: "rgba(15, 23, 42, 0.4)",
                    borderRadius: '50%',
                    marginBottom: 16
                  }}>{creating ? <span>...</span> : "+"}</div>
                  <div style={{
                    color: "var(--text)",
                    fontWeight: 600,
                    fontSize: 18,
                    textAlign: "center"
                  }}>Create new</div>
                </div>
              </div>
            ) : error ? (
              <p style={{ marginTop: 20, color: 'var(--text-secondary)' }}>
                Please resolve the issue above or try refreshing the page.
              </p>
            ) : null}
          </div>
        ) : error ? (
          <p style={{ marginTop: 20, color: 'var(--text-secondary)' }}>
            Please resolve the issue above or try refreshing the page.
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default Dashboard;